import { useNavigate, useParams } from 'react-router-dom'
import { Table, useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import type { CoinTransaction } from '../../api/types'
import { PageState } from '../../components/PageState'
import { TestFundsBanner } from '../../components/TestFundsBanner'
import { describeLoadError } from '../../lib/errors'
import { numericSortKey, utcDateTime } from '../../lib/format'

const columns: Column<CoinTransaction>[] = [
  { key: 'createdAt', label: 'Date', align: 'left', format: (v) => utcDateTime(v as string) },
  {
    key: 'cause',
    label: 'Side',
    align: 'left',
    format: (v) => <span className={`wnf-badge wnf-badge--${v === 'buy' ? 'buy' : 'sell'}`}>{String(v).toUpperCase()}</span>,
  },
  { key: 'amount1', label: 'Amount (base)', numeric: true, cellClass: () => 'wnf-num', sortValue: (r) => numericSortKey(r.amount1) },
  { key: 'amount2', label: 'Amount (quote)', numeric: true, cellClass: () => 'wnf-num', sortValue: (r) => numericSortKey(r.amount2) },
  { key: 'price', label: 'Price', numeric: true, cellClass: () => 'wnf-num', sortValue: (r) => numericSortKey(r.price) },
  { key: 'fee1', label: 'Fee', numeric: true, cellClass: () => 'wnf-num', format: (_v, r) => `${r.fee1} / ${r.fee2}`, sortValue: (r) => numericSortKey(r.fee1) },
  {
    key: 'result',
    label: 'Result',
    align: 'left',
    numeric: true,
    format: (v) => (v === 0 ? 'SUCCESS' : `Error code #${String(v)}`),
  },
  { key: 'reference', label: 'Reference', align: 'left' },
]

function PairPicker({ current }: { current: string }) {
  const navigate = useNavigate()
  const prices = useApiQuery(() => wnfClient.searchPrices(), [])
  const pairs = Object.values(prices.data ?? {})
    .map((p) => `${p.coin1}/${p.coin2}`)
    .sort()
  return (
    <label className="wnf-inline-field">
      Pair
      <select
        value={current}
        onChange={(e) => {
          const v = e.target.value
          if (v) navigate(`/orders/history/${v}`)
        }}
        aria-label="Coin pair"
      >
        <option value="">Select a pair…</option>
        {current && !pairs.includes(current) && <option value={current}>{current}</option>}
        {pairs.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    </label>
  )
}

function History({ coin1, coin2 }: { coin1: string; coin2: string }) {
  const { data, loading, error } = useApiQuery(() => wnfClient.searchOrders(coin1, coin2), [coin1, coin2])
  return (
    <PageState loading={loading} error={error ? describeLoadError(error, 'order history') : null}>
      {data && data.length === 0 && <p className="wnf-muted">No filled orders for {coin1}/{coin2}.</p>}
      {data && data.length > 0 && (
        <Table columns={columns} rows={data} rowKey={(r) => r.id} initialSort={{ key: 'createdAt', dir: 'desc' }} />
      )}
    </PageState>
  )
}

/** Order history per pair (`GET /order/spot/{coin1}/{coin2}`) — the same
 * filled-order list the spotbot reads, shown to the account owner. */
export function OrderHistoryPage() {
  const { coin1, coin2 } = useParams()
  const c1 = coin1?.toUpperCase()
  const c2 = coin2?.toUpperCase()
  return (
    <div>
      <div className="wnf-page-title">
        <h1>Order history{c1 && c2 ? ` — ${c1}/${c2}` : ''}</h1>
      </div>
      <TestFundsBanner />
      <div className="wnf-toolbar">
        <PairPicker current={c1 && c2 ? `${c1}/${c2}` : ''} />
      </div>
      {c1 && c2 ? <History coin1={c1} coin2={c2} /> : <p className="wnf-muted">Select a pair to see its filled orders.</p>}
    </div>
  )
}
